import { useMemo } from 'react';
import useSWR from 'swr';
import type { ActivityType } from 'types';
import type { EmbeddedProduct } from 'modules/supplier/products';
import {
  PRODUCT_DISCOVERY_PAGE_SIZE,
  type ChainPath,
  type ProductDiscoveryPage,
  type ProductDiscoveryRequest,
} from 'modules/supplier/supplier/components/ProductChainPicker';
import { expandClientTargetPrefixes } from './clientTarget';
import { pathFromProduct } from './tree';
import type { ClientTargetPath } from './types';
import type { ClientDiscountOwner } from './owner';
import type { ClientDiscountRootOwner } from '../vendorTerms/types';

/** Discovery deliberately consumes one bounded page; it never follows pagination. */
export type ClientTargetPage = ProductDiscoveryPage;

export type ClientTargetRequest = ProductDiscoveryRequest;

/**
 * Build the bounded shared product request used for client target discovery.
 * Availability and provider filters intentionally do not belong in this request.
 */
export function clientTargetRequest(
  type: ActivityType,
  search: string,
  owner?: ClientDiscountOwner,
  kind: ClientDiscountRootOwner['kind'] = owner?.kind ?? 'vendor',
): ClientTargetRequest {
  return {
    url: 'supplier/products/',
    method: 'GET',
    queryParams: {
      type,
      search,
      disabled: false,
      page_size: PRODUCT_DISCOVERY_PAGE_SIZE,
      kind: kind === 'vendor' ? 'MEDIA' : 'SERVICE',
      ...owner?.discoveryFilter,
    },
  };
}

export type ClientTargetCatalogue = {
  /** Full product paths used to render discovery navigation. */
  paths: readonly ChainPath[];
  /** Vendor/outlet/product prefixes that may be selected as endpoints. */
  targetPaths: readonly ClientTargetPath[];
  productsById: Map<number, EmbeddedProduct>;
};

/**
 * Expand discoverable media and service products into selectable prefixes.
 */
export function targetPathsFromProducts(products: readonly EmbeddedProduct[]): readonly ClientTargetPath[] {
  return toClientTargetCatalogue(products).targetPaths;
}

export function toClientTargetCatalogue(products: readonly EmbeddedProduct[]): ClientTargetCatalogue {
  const validProducts: EmbeddedProduct[] = [];
  const paths: ChainPath[] = [];

  for (const product of products) {
    try {
      const outlet = product.outlet_or_service_category;
      if (outlet.type != null && outlet.type !== product.type) continue;
      // Validate both kind and ancestry before exposing the row to the picker.
      const path = pathFromProduct(product);
      validProducts.push(product);
      paths.push(path);
    } catch {
      // See targetPathsFromProducts.
    }
  }

  return {
    paths,
    targetPaths: expandClientTargetPrefixes(paths, { replaceDuplicates: false }),
    productsById: new Map(validProducts.map((product) => [product.id, product])),
  };
}

const EMPTY_PAGE: ClientTargetPage = { count: 0, results: [] };

export type ClientTargetDiscovery = ClientTargetCatalogue & {
  count: number;
  returnedCount: number;
  isTruncated: boolean;
  isLoading: boolean;
  error: unknown;
};

/**
 * Fetch client target discovery. Previous data is not retained when the
 * type or search changes, so results for an old type can never be selected as
 * targets for the new section.
 */
export function useClientTargetDiscovery(
  type: ActivityType,
  search: string,
  owner?: ClientDiscountOwner,
  kind: ClientDiscountRootOwner['kind'] = owner?.kind ?? 'vendor',
): ClientTargetDiscovery {
  const request = clientTargetRequest(type, search, owner, kind);
  const { data, error, isLoading } = useSWR<ClientTargetPage>(request);
  const {
    data: roots,
    error: rootError,
    isLoading: rootsLoading,
  } = useSWR<{
    count: number;
    results: { id: number; name: string; type?: ActivityType | null }[];
  }>(
    owner
      ? null
      : {
          url: kind === 'vendor' ? 'supplier/vendors/' : 'supplier/outlet_or_service_category/',
          method: 'GET',
          queryParams: {
            search,
            product_type: type,
            page_size: PRODUCT_DISCOVERY_PAGE_SIZE,
            ...(kind === 'service-category' ? { kind: 'SERVICE_CATEGORY' } : {}),
          },
        },
  );
  const page = data ?? EMPTY_PAGE;

  const catalogue = useMemo(() => toClientTargetCatalogue(page.results), [page.results]);
  const rootPaths: ClientTargetPath[] = owner
    ? [owner.rootPath]
    : (roots?.results ?? [])
        .filter((root) => kind === 'vendor' || root.type == null || root.type === type)
        .map((root) =>
          kind === 'vendor'
            ? [{ kind, id: root.id, label: root.name }]
            : [{ kind, id: root.id, label: root.name, type: root.type ?? null }],
        );
  const paths = [...rootPaths, ...catalogue.paths];

  return {
    ...catalogue,
    paths,
    targetPaths: expandClientTargetPrefixes(paths, { replaceDuplicates: false }),
    count: page.count,
    returnedCount: page.results.length,
    isTruncated: page.count > page.results.length || (roots != null && roots.count > roots.results.length),
    isLoading: isLoading || rootsLoading,
    error: error || rootError,
  };
}
